import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface Version {
  id: number;
  rule_id: string;
  version: number;
  created_at: string;
  created_by: string;
  created_by_name?: string;
  change_summary?: string;
  content_size?: number;
}

interface VersionContent {
  id: number;
  rule_id: string;
  version: number;
  content: any;
  created_at: string;
  created_by: string;
  created_by_name?: string;
  change_summary?: string;
}

interface VersionHistoryProps {
  ruleId: string;
  ruleName: string;
  currentVersion?: number;
  onRollback?: (version: number) => void;
  onClose: () => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  ruleId,
  ruleName,
  currentVersion,
  onRollback,
  onClose
}) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState<any>(null);
  const [viewingVersion, setViewingVersion] = useState<VersionContent | null>(null);
  const [rollbackInProgress, setRollbackInProgress] = useState(false);

  useEffect(() => {
    loadVersions();
  }, [ruleId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/rules/${ruleId}/versions`);
      setVersions((response.data as any).versions || []);
    } catch (error) {
      console.error('Error loading versions:', error);
      alert('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const viewVersion = async (version: number) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/rules/${ruleId}/versions/${version}`);
      setViewingVersion((response.data as any).version);
    } catch (error) {
      console.error('Error loading version:', error);
      alert('Failed to load version details');
    } finally {
      setLoading(false);
    }
  };

  const compareVersions = async () => {
    if (selectedVersions.length !== 2) {
      alert('Please select exactly 2 versions to compare');
      return;
    }

    try {
      setComparing(true);
      const [v1, v2] = selectedVersions.sort((a, b) => a - b);
      const response = await axios.get(`${API_BASE}/rules/${ruleId}/diff/${v1}/${v2}`);
      setComparison(response.data);
    } catch (error) {
      console.error('Error comparing versions:', error);
      alert('Failed to compare versions');
    } finally {
      setComparing(false);
    }
  };

  const rollbackToVersion = async (version: number) => {
    if (!window.confirm(`Are you sure you want to rollback to version ${version}? This will create a new version with the content from version ${version}.`)) {
      return;
    }

    try {
      setRollbackInProgress(true);
      await axios.post(`${API_BASE}/rules/${ruleId}/rollback/${version}`, {
        changeSummary: `Rolled back to version ${version}`
      });
      alert(`Successfully rolled back to version ${version}`);
      await loadVersions();
      if (onRollback) {
        onRollback(version);
      }
    } catch (error) {
      console.error('Error rolling back:', error);
      alert('Failed to rollback to version');
    } finally {
      setRollbackInProgress(false);
    }
  };

  const toggleVersionSelection = (version: number) => {
    if (selectedVersions.includes(version)) {
      setSelectedVersions(selectedVersions.filter(v => v !== version));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, version]);
    } else {
      // Replace the first selected version
      setSelectedVersions([selectedVersions[1], version]);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-large version-history-modal">
        <div className="modal-header">
          <h3>Version History: {ruleName}</h3>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>

        <div className="version-history-content">
          {loading && <div className="loading">Loading...</div>}

          {/* Version List */}
          {!viewingVersion && !comparison && (
            <div className="version-list-section">
              <div className="version-actions">
                <button
                  className="btn-primary"
                  onClick={compareVersions}
                  disabled={selectedVersions.length !== 2 || comparing}
                >
                  {comparing ? 'Comparing...' : `Compare Selected (${selectedVersions.length}/2)`}
                </button>
                {selectedVersions.length > 0 && (
                  <button
                    className="btn-secondary"
                    onClick={() => setSelectedVersions([])}
                  >
                    Clear Selection
                  </button>
                )}
              </div>

              {versions.length === 0 ? (
                <p className="empty-state">No versions found for this rule</p>
              ) : (
                <table className="management-table version-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>Select</th>
                      <th style={{ width: '80px' }}>Version</th>
                      <th>Date</th>
                      <th>Created By</th>
                      <th>Summary</th>
                      <th style={{ width: '80px' }}>Size</th>
                      <th style={{ width: '150px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((v) => (
                      <tr
                        key={v.version}
                        className={`${v.version === currentVersion ? 'current-version' : ''} ${selectedVersions.includes(v.version) ? 'selected' : ''}`}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedVersions.includes(v.version)}
                            onChange={() => toggleVersionSelection(v.version)}
                          />
                        </td>
                        <td>
                          <strong>v{v.version}</strong>
                          {v.version === currentVersion && (
                            <span className="current-badge">Current</span>
                          )}
                        </td>
                        <td>{formatDate(v.created_at)}</td>
                        <td>{v.created_by_name || v.created_by}</td>
                        <td className="change-summary">{v.change_summary || '-'}</td>
                        <td>{v.content_size ? formatSize(v.content_size) : '-'}</td>
                        <td className="version-actions-cell">
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => viewVersion(v.version)}
                          >
                            View
                          </button>
                          {v.version !== currentVersion && (
                            <button
                              className="btn-small btn-warning"
                              onClick={() => rollbackToVersion(v.version)}
                              disabled={rollbackInProgress}
                            >
                              Rollback
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Version Detail View */}
          {viewingVersion && !comparison && (
            <div className="version-detail-section">
              <div className="version-detail-header">
                <button className="btn-secondary" onClick={() => setViewingVersion(null)}>
                  &larr; Back to List
                </button>
                <h4>Version {viewingVersion.version}</h4>
              </div>

              <div className="version-meta">
                <p><strong>Created:</strong> {formatDate(viewingVersion.created_at)}</p>
                <p><strong>By:</strong> {viewingVersion.created_by_name || viewingVersion.created_by}</p>
                {viewingVersion.change_summary && (
                  <p><strong>Summary:</strong> {viewingVersion.change_summary}</p>
                )}
              </div>

              <div className="version-content-preview">
                <h5>Rule Content</h5>
                <pre className="json-preview">
                  {JSON.stringify(viewingVersion.content, null, 2)}
                </pre>
              </div>

              <div className="version-detail-actions">
                <button
                  className="btn-warning"
                  onClick={() => rollbackToVersion(viewingVersion.version)}
                  disabled={rollbackInProgress || viewingVersion.version === currentVersion}
                >
                  {rollbackInProgress ? 'Rolling back...' : 'Rollback to This Version'}
                </button>
              </div>
            </div>
          )}

          {/* Comparison View */}
          {comparison && (
            <div className="comparison-section">
              <div className="comparison-header">
                <button className="btn-secondary" onClick={() => setComparison(null)}>
                  &larr; Back to List
                </button>
                <h4>
                  Comparing Version {comparison.version1.version} → Version {comparison.version2.version}
                </h4>
              </div>

              <div className="comparison-stats">
                <div className="stat-card">
                  <span className="stat-label">Nodes</span>
                  <span className="stat-value">
                    {comparison.stats.v1NodeCount} → {comparison.stats.v2NodeCount}
                    <span className={`stat-diff ${comparison.stats.nodesDiff > 0 ? 'positive' : comparison.stats.nodesDiff < 0 ? 'negative' : ''}`}>
                      ({comparison.stats.nodesDiff > 0 ? '+' : ''}{comparison.stats.nodesDiff})
                    </span>
                  </span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Edges</span>
                  <span className="stat-value">
                    {comparison.stats.v1EdgeCount} → {comparison.stats.v2EdgeCount}
                    <span className={`stat-diff ${comparison.stats.edgesDiff > 0 ? 'positive' : comparison.stats.edgesDiff < 0 ? 'negative' : ''}`}>
                      ({comparison.stats.edgesDiff > 0 ? '+' : ''}{comparison.stats.edgesDiff})
                    </span>
                  </span>
                </div>
              </div>

              <div className="comparison-panels">
                <div className="comparison-panel">
                  <h5>Version {comparison.version1.version}</h5>
                  <p className="version-info">
                    {formatDate(comparison.version1.created_at)} by {comparison.version1.created_by_name || comparison.version1.created_by}
                  </p>
                  <pre className="json-preview">
                    {JSON.stringify(comparison.version1.content, null, 2)}
                  </pre>
                </div>
                <div className="comparison-panel">
                  <h5>Version {comparison.version2.version}</h5>
                  <p className="version-info">
                    {formatDate(comparison.version2.created_at)} by {comparison.version2.created_by_name || comparison.version2.created_by}
                  </p>
                  <pre className="json-preview">
                    {JSON.stringify(comparison.version2.content, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionHistory;
